import { AlertTriangle, Command, X, XCircle } from "lucide-react";

import { ColocaButton } from "@/shared/components/buttons/ColocaButton";
import { ColocaDialog, ColocaDialogClose } from "@/shared/components/overlays/ColocaDialog";
import { useTranslations } from "@/shared/lang/useTranslations";

interface DisableApiKeyDialogProps {
  open: boolean;
  apiKeyName: string;
  apiKeyPrefix?: string;
  onConfirm: () => void;
  onCancel: () => void;
}

export function DisableApiKeyDialog({
  open,
  apiKeyName,
  apiKeyPrefix,
  onConfirm,
  onCancel,
}: DisableApiKeyDialogProps) {
  const { t } = useTranslations("settings");

  return (
    <ColocaDialog
      open={open}
      onOpenChange={(isOpen) => {
        if (!isOpen) onCancel();
      }}
      title={
        <span className="flex items-center gap-2">
          <AlertTriangle size={16} className="text-warning" />
          {t("api_keys_disable_title")}
        </span>
      }
      description={t("api_keys_disable_subtitle")}
      size="default"
      footer={
        <>
          <ColocaDialogClose
            render={<ColocaButton variant="ghost">{t("api_keys_disable_cancel")}</ColocaButton>}
          />
          <ColocaButton variant="destructive" icon={XCircle} onClick={onConfirm}>
            {t("api_keys_disable_confirm")}
          </ColocaButton>
        </>
      }
    >
      <div className="space-y-3.5">
        {/* Key identity */}
        <div className="flex items-center gap-2.5 rounded-sm border border-border bg-muted/40 px-3 py-2.5">
          <span className="grid h-[22px] w-[22px] shrink-0 place-items-center rounded border border-border bg-card text-muted-foreground">
            <Command size={11} />
          </span>
          <div className="min-w-0 flex-1">
            <div className="text-[13px] font-semibold text-foreground">{apiKeyName}</div>
            {apiKeyPrefix && (
              <div className="font-mono text-[11px] text-muted-foreground">{apiKeyPrefix}</div>
            )}
          </div>
        </div>

        {/* Impact warning */}
        <div className="rounded-sm border border-warning/40 bg-warning/10 p-3.5">
          <div className="mb-2 flex items-center gap-1.5">
            <AlertTriangle size={13} className="text-warning" />
            <span className="text-[12.5px] font-semibold text-foreground">
              {t("api_keys_disable_impact_title")}
            </span>
          </div>
          <ul className="m-0 list-none space-y-1.5 p-0 text-[12px] text-foreground/90">
            <li className="flex items-start gap-2">
              <X size={12} className="mt-0.5 shrink-0 text-destructive" />
              <span>{t("api_keys_disable_impact_unauthorized")}</span>
            </li>
            <li className="flex items-start gap-2">
              <X size={12} className="mt-0.5 shrink-0 text-destructive" />
              <span>{t("api_keys_disable_impact_integrations")}</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="mt-0.5 shrink-0 text-primary">✓</span>
              <span>{t("api_keys_disable_impact_reversible")}</span>
            </li>
          </ul>
        </div>
      </div>
    </ColocaDialog>
  );
}
